import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Paper, Typography } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type DiagnosisChapterPoint = {
  chapter: string;
  claim_item_count: number;
  avg_paid: number;
  total_paid: number;
};

type Props = {
  data: DiagnosisChapterPoint[];
  isFullscreen: boolean;
};

// Cap to the top chapters by spend so the bars stay legible in the fixed-height
// card; the backend already orders by total_paid DESC.
const MAX_BARS = 12;

const ChapterTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: DiagnosisChapterPoint }[] }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper elevation={3} sx={{ p: 1.5, maxWidth: 280 }}>
      <Typography variant="subtitle2">{p.chapter}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Total paid: <strong>{formatUSD(p.total_paid)}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {p.claim_item_count.toLocaleString()} lines · avg {formatUSD(p.avg_paid)} / line
      </Typography>
    </Paper>
  );
};

/**
 * Underlying medical spend rolled up to the ICD-10-CM chapter (coarse clinical
 * category, derived natively from the code). Horizontal bars ranked by total
 * paid — the actionable "which body system is driving cost" view that the sparse
 * per-code chart can't give at a glance.
 */
const DiagnosisChapterChart = ({ data, isFullscreen }: Props) => {
  const palette = useChartPalette();
  if (!data.length) return <EmptyChartState />;

  const rows = data.slice(0, MAX_BARS);

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <BarChart data={rows} layout="vertical" margin={{ top: 10, right: 24, left: 10, bottom: 10 }}>
        <defs>
          <linearGradient id="chapterFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="5%" stopColor={palette[0]} stopOpacity={0.35} />
            <stop offset="95%" stopColor={palette[0]} stopOpacity={0.9} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--mui-palette-divider, rgba(0,0,0,0.08))" />
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12 }}
          tickFormatter={(v: number) => formatUSD(v)}
        />
        <YAxis
          type="category"
          dataKey="chapter"
          width={150}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11 }}
        />
        <Tooltip content={<ChapterTooltip />} cursor={{ fill: "var(--mui-palette-action-hover, rgba(0,0,0,0.04))" }} />
        <Bar dataKey="total_paid" fill="url(#chapterFill)" radius={[0, 4, 4, 0]}>
          {rows.map((r) => (
            <Cell key={r.chapter} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DiagnosisChapterChart;
